import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'state/store';
import { fetchCurrentUserDetailsAsync } from 'state/authentication/authenticationSlice';
import {
  Box, Typography, TextField, Card, CardContent, Avatar, Divider, useTheme,
  Button, Switch, FormControlLabel, IconButton, Alert, CircularProgress,
  Snackbar, Tooltip, useMediaQuery, Badge, Dialog, DialogTitle, DialogContent,
  DialogActions, Slider, Chip,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { deepPurple } from '@mui/material/colors';
import {
  Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon, Delete as DeleteIcon,
  ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon,
  RotateLeft as RotateLeftIcon, RotateRight as RotateRightIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import { CamelCaseToName } from 'utils/CamelCaseToName';
import { apiClient } from 'utils/ApiClient';
import { LocalStorageAuth } from 'utils/LocalStorageAuth';
import { API_BASE_URL } from 'envConfig';

interface CountryOption {
  code: string;
  label: string;
  dialCode: string;
}

interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MembershipInfo {
  companyId: string;
  companyName: string;
  isPrimary: boolean;
  moduleAndRole: Array<{ module: string; role: string }>;
  joinedAt?: string;
  status?: string;
}

/** Server GET /auth/countries returns the same shape as st-ck-server `COUNTRIES_LIST` (envConfig). */
const filterCountryOptions = createFilterOptions<CountryOption>({
  stringify: (option) => `${option.label} ${option.code}`,
});

const getCroppedImg = (imageSrc: string, pixelCrop: CroppedAreaPixels, rotation: number = 0): Promise<{ base64: string; mime: string }> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context unavailable')); return; }

      const radians = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(radians));
      const cos = Math.abs(Math.cos(radians));
      const rotW = image.width * cos + image.height * sin;
      const rotH = image.width * sin + image.height * cos;

      canvas.width = rotW;
      canvas.height = rotH;
      ctx.translate(rotW / 2, rotH / 2);
      ctx.rotate(radians);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      ctx.putImageData(data, 0, 0);

      const outputSize = 300;
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = outputSize;
      finalCanvas.height = outputSize;
      const fCtx = finalCanvas.getContext('2d');
      if (!fCtx) { reject(new Error('Canvas context unavailable')); return; }
      fCtx.drawImage(canvas, 0, 0, pixelCrop.width, pixelCrop.height, 0, 0, outputSize, outputSize);

      const base64 = finalCanvas.toDataURL('image/jpeg', 0.9).split(',')[1];
      resolve({ base64, mime: 'image/jpeg' });
    };
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
};

export const Profile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.authentication.user);

  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingNotifications, setEditingNotifications] = useState(false);
  const [savingSection, setSavingSection] = useState<null | 'personal' | 'address' | 'notifications'>(null);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [memberships, setMemberships] = useState<MembershipInfo[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgLoadAttemptedRef = useRef(false);
  const membershipsFetchedRef = useRef(false);

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    country: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    zip: '',
    dob: '',
    emailNotifications: true,
    smsNotifications: true,
    whatsAppNotifications: true,
  });

  const populateForm = useCallback(() => {
    if (!user) return;
    setFormData({
      firstName: user.profile?.firstName || '',
      lastName: user.profile?.lastName || '',
      country: user.profile?.country || '',
      addressLine1: user.profile?.addressLine1 || '',
      addressLine2: user.profile?.addressLine2 || '',
      city: user.profile?.city || '',
      zip: user.profile?.zip || '',
      dob: user.profile?.dob ? new Date(user.profile.dob).toISOString().split('T')[0] : '',
      emailNotifications: user.profile?.emailNotifications ?? true,
      smsNotifications: user.profile?.smsNotifications ?? true,
      whatsAppNotifications: user.profile?.whatsAppNotifications ?? true,
    });
  }, [user]);

  useEffect(() => { populateForm(); }, [populateForm]);

  useEffect(() => {
    if (!editingAddress) return;
    let cancelled = false;
    setCountriesLoading(true);
    (async () => {
      try {
        const res = await apiClient.get('/auth/countries');
        const list =
          res && typeof res === 'object' && Array.isArray((res as { data?: unknown }).data)
            ? (res as { data: CountryOption[] }).data
            : Array.isArray(res)
              ? (res as CountryOption[])
              : [];
        if (!cancelled) setCountries(list);
      } catch {
        /* keep existing countries / empty; user can retry by toggling edit */
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editingAddress]);

  useEffect(() => {
    if (!user?._id || membershipsFetchedRef.current) return;
    membershipsFetchedRef.current = true;
    const fetchMemberships = async () => {
      try {
        const data = await apiClient.get(`/user/${user._id}/memberships`);
        if (data?.data && Array.isArray(data.data)) setMemberships(data.data);
        else if (Array.isArray(data)) setMemberships(data as any);
      } catch { /* memberships not critical for profile display */ }
    };
    fetchMemberships();
  }, [user?._id]);

  useEffect(() => {
    if (!user?._id || imgLoadAttemptedRef.current || !user?.profile?.hasProfileImage) return;
    imgLoadAttemptedRef.current = true;
    const loadProfileImage = async () => {
      try {
        const token = LocalStorageAuth.getToken() || '';
        const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` };
        const activeCompanyId = LocalStorageAuth.getActiveCompanyId();
        if (activeCompanyId) headers['X-Active-Company-Id'] = activeCompanyId;
        const resp = await fetch(`${API_BASE_URL}/user/${user._id}/profile-image`, { headers });
        if (resp.ok) {
          const blob = await resp.blob();
          setProfileImageUrl(URL.createObjectURL(blob));
        }
      } catch { /* no profile image */ }
    };
    loadProfileImage();
  }, [user?._id]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleToggle = (field: string) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const personalFieldsValid = useMemo(() => (
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.dob.trim() !== ''
  ), [formData]);

  const addressFieldsValid = useMemo(() => (
    formData.city.trim() !== '' &&
    formData.country.trim() !== '' &&
    formData.zip.trim() !== ''
  ), [formData]);

  const countryOptions = useMemo(() => {
    if (!editingAddress) return countries;
    const c = formData.country.trim();
    if (c && !countries.some((x) => x.label === c)) {
      return [{ code: '_legacy', label: c, dialCode: '' }, ...countries];
    }
    return countries;
  }, [editingAddress, countries, formData.country]);

  const selectedCountryOption = useMemo(() => {
    if (!formData.country.trim()) return null;
    return countryOptions.find((x) => x.label === formData.country) ?? null;
  }, [countryOptions, formData.country]);

  const sectionIconSx = {
    bgcolor: theme.palette.secondary.main,
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    '&:hover': { bgcolor: theme.palette.secondary.dark },
    width: 36, height: 36,
  } as const;

  const beginEditPersonal = () => {
    populateForm();
    setEditingAddress(false);
    setEditingNotifications(false);
    setEditingPersonal(true);
  };

  const beginEditAddress = () => {
    populateForm();
    setEditingPersonal(false);
    setEditingNotifications(false);
    setEditingAddress(true);
  };

  const beginEditNotifications = () => {
    populateForm();
    setEditingPersonal(false);
    setEditingAddress(false);
    setEditingNotifications(true);
  };

  const handleSavePersonal = async () => {
    if (!user?._id || !personalFieldsValid) return;
    setSavingSection('personal');
    try {
      await apiClient.patch(`/user/${user._id}/my-profile`, {
        'profile.firstName': formData.firstName,
        'profile.lastName': formData.lastName,
        'profile.dob': formData.dob,
      });
      await dispatch(fetchCurrentUserDetailsAsync(user._id));
      setEditingPersonal(false);
      setSnackbar({ open: true, message: 'Personal information updated', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to update profile', severity: 'error' });
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveAddress = async () => {
    if (!user?._id || !addressFieldsValid) return;
    setSavingSection('address');
    try {
      await apiClient.patch(`/user/${user._id}/my-profile`, {
        'profile.country': formData.country,
        'profile.addressLine1': formData.addressLine1,
        'profile.addressLine2': formData.addressLine2,
        'profile.city': formData.city,
        'profile.zip': formData.zip,
      });
      await dispatch(fetchCurrentUserDetailsAsync(user._id));
      setEditingAddress(false);
      setSnackbar({ open: true, message: 'Address updated', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to update address', severity: 'error' });
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveNotifications = async () => {
    if (!user?._id) return;
    setSavingSection('notifications');
    try {
      await apiClient.patch(`/user/${user._id}/my-profile`, {
        'profile.emailNotifications': formData.emailNotifications,
        'profile.smsNotifications': formData.smsNotifications,
        'profile.whatsAppNotifications': formData.whatsAppNotifications,
      });
      await dispatch(fetchCurrentUserDetailsAsync(user._id));
      setEditingNotifications(false);
      setSnackbar({ open: true, message: 'Notification preferences updated', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to update notifications', severity: 'error' });
    } finally {
      setSavingSection(null);
    }
  };

  const cancelPersonal = () => { populateForm(); setEditingPersonal(false); };
  const cancelAddress = () => { populateForm(); setEditingAddress(false); };
  const cancelNotifications = () => { populateForm(); setEditingNotifications(false); };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({ open: true, message: 'Unsupported image type. Allowed: jpeg, png, gif, webp', severity: 'error' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Image must be less than 10MB', severity: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); setCroppedAreaPixels(null);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onCropComplete = useCallback((_: any, croppedPixels: CroppedAreaPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !user?._id) return;
    setImageUploading(true);
    setCropDialogOpen(false);
    try {
      const { base64, mime } = await getCroppedImg(cropImageSrc, croppedAreaPixels, rotation);
      await apiClient.put(`/user/${user._id}/profile-image`, { image: base64, mime });
      setProfileImageUrl(`data:${mime};base64,${base64}`);
      await dispatch(fetchCurrentUserDetailsAsync(user._id));
      setSnackbar({ open: true, message: 'Profile picture updated', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to upload image', severity: 'error' });
    } finally {
      setImageUploading(false);
      setCropImageSrc(null);
    }
  };

  const handleCropCancel = () => { setCropDialogOpen(false); setCropImageSrc(null); };

  const handleImageDelete = async () => {
    if (!user?._id) return;
    setImageUploading(true);
    try {
      await apiClient.delete(`/user/${user._id}/profile-image`);
      setProfileImageUrl(null);
      await dispatch(fetchCurrentUserDetailsAsync(user._id));
      setSnackbar({ open: true, message: 'Profile picture removed', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to remove image', severity: 'error' });
    } finally {
      setImageUploading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h5" color="textSecondary">Please log in to view your profile.</Typography>
      </Box>
    );
  }

  const avatarSrc = profileImageUrl || user.profile?.avatar || 'https://www.gravatar.com/avatar';
  const initials = `${(user.profile?.firstName || 'U').charAt(0)}${(user.profile?.lastName || '').charAt(0)}`.toUpperCase();

  const formattedDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    try { return new Date(dateString).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return dateString; }
  };

  const formattedDateTime = (dateString: string | undefined) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    } catch { return dateString; }
  };

  const savingAny = savingSection !== null;
  const personalSaveDisabled = savingAny || !personalFieldsValid;
  const addressSaveDisabled = savingAny || !addressFieldsValid;

  const sectionEditActions = (
    editing: boolean,
    onEdit: () => void,
    onSave: () => void,
    onCancel: () => void,
    saveDisabled: boolean,
    section: 'personal' | 'address' | 'notifications',
  ) => {
    const busy = savingSection === section;
    if (!editing) {
      return (
        <Tooltip title="Edit">
          <IconButton onClick={onEdit} size="small" disabled={savingAny} sx={sectionIconSx}>
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <>
        <Tooltip title={saveDisabled && !busy ? 'Please fill all mandatory fields' : 'Save'}>
          <span>
            <IconButton
              onClick={onSave}
              disabled={saveDisabled}
              size="small"
              sx={{
                bgcolor: theme.palette.success.main, color: '#fff',
                '&:hover': { bgcolor: theme.palette.success.dark },
                '&.Mui-disabled': { bgcolor: theme.palette.action.disabledBackground },
                width: 36, height: 36,
              }}
            >
              {busy ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel">
          <IconButton
            onClick={onCancel}
            disabled={busy}
            size="small"
            sx={{
              bgcolor: theme.palette.grey[500], color: '#fff',
              '&:hover': { bgcolor: theme.palette.grey[700] },
              '&.Mui-disabled': { bgcolor: theme.palette.action.disabledBackground },
              width: 36, height: 36,
            }}
          >
            <CancelIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} pr={{ xs: 1.25, sm: 2.5 }} width="100%" pb={4}>
      <Box mb={3}>
        <Typography variant="h4">User Profile</Typography>
      </Box>

      <Card sx={{ maxWidth: 800, margin: 'auto', p: { xs: 1.5, sm: 3 }, borderRadius: '12px', boxShadow: 3 }}>
        <CardContent>
          {/* Avatar Section */}
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <Box position="relative" display="inline-flex">
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="Upload photo">
                      <IconButton
                        size="small" onClick={() => fileInputRef.current?.click()} disabled={imageUploading}
                        sx={{ bgcolor: theme.palette.primary.main, color: '#fff', '&:hover': { bgcolor: theme.palette.primary.dark }, width: 32, height: 32 }}
                      >
                        {imageUploading ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                    {profileImageUrl && (
                      <Tooltip title="Remove photo">
                        <IconButton
                          size="small" onClick={handleImageDelete} disabled={imageUploading}
                          sx={{ bgcolor: theme.palette.error.main, color: '#fff', '&:hover': { bgcolor: theme.palette.error.dark }, width: 32, height: 32 }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
              >
                <Avatar
                  alt={initials} src={avatarSrc}
                  sx={{
                    width: 120, height: 120, bgcolor: deepPurple[500], fontSize: '3rem',
                    border: `3px solid ${theme.palette.primary.main}`,
                    boxShadow: `0px 0px 12px ${theme.palette.secondary.main}`,
                  }}
                >
                  {initials}
                </Avatar>
              </Badge>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" style={{ display: 'none' }} onChange={handleImageSelect} />
            </Box>
            <Typography variant="h5" mt={2}>
              {`${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`}
            </Typography>
            <Typography variant="body1" color="textSecondary">{user.email}</Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ── Personal Information ── */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">Personal Information</Typography>
            <Box display="flex" gap={0.5}>
              {sectionEditActions(
                editingPersonal,
                beginEditPersonal,
                handleSavePersonal,
                cancelPersonal,
                personalSaveDisabled,
                'personal',
              )}
            </Box>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              <TextField label="First Name *" value={editingPersonal ? formData.firstName : (user.profile?.firstName || '')}
                onChange={handleChange('firstName')} fullWidth margin="normal"
                InputProps={{ readOnly: !editingPersonal }} variant="outlined" size="small"
                error={editingPersonal && formData.firstName.trim() === ''} helperText={editingPersonal && formData.firstName.trim() === '' ? 'Required' : ''} />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              <TextField label="Last Name *" value={editingPersonal ? formData.lastName : (user.profile?.lastName || '')}
                onChange={handleChange('lastName')} fullWidth margin="normal"
                InputProps={{ readOnly: !editingPersonal }} variant="outlined" size="small"
                error={editingPersonal && formData.lastName.trim() === ''} helperText={editingPersonal && formData.lastName.trim() === '' ? 'Required' : ''} />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              <TextField
                label="Email"
                value={user.email || ''}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                variant="outlined"
                size="small"
                type="email"
                helperText="Email cannot be changed here"
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              {editingPersonal ? (
                <TextField label="Date of Birth *" value={formData.dob}
                  onChange={handleChange('dob')} fullWidth margin="normal"
                  variant="outlined" size="small" type="date" InputLabelProps={{ shrink: true }}
                  error={formData.dob.trim() === ''} helperText={formData.dob.trim() === '' ? 'Required' : ''} />
              ) : (
                <TextField label="Date of Birth" value={formattedDate(user.profile?.dob)}
                  fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ── Address ── */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">Address</Typography>
            <Box display="flex" gap={0.5}>
              {sectionEditActions(
                editingAddress,
                beginEditAddress,
                handleSaveAddress,
                cancelAddress,
                addressSaveDisabled,
                'address',
              )}
            </Box>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box sx={{ width: '100%' }}>
              <TextField label="Address Line 1" value={editingAddress ? formData.addressLine1 : (user.profile?.addressLine1 || '')}
                onChange={handleChange('addressLine1')} fullWidth margin="normal"
                InputProps={{ readOnly: !editingAddress }} variant="outlined" size="small" />
            </Box>
            <Box sx={{ width: '100%' }}>
              <TextField label="Address Line 2" value={editingAddress ? formData.addressLine2 : (user.profile?.addressLine2 || '')}
                onChange={handleChange('addressLine2')} fullWidth margin="normal"
                InputProps={{ readOnly: !editingAddress }} variant="outlined" size="small" />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              <TextField label="City *" value={editingAddress ? formData.city : (user.profile?.city || '')}
                onChange={handleChange('city')} fullWidth margin="normal"
                InputProps={{ readOnly: !editingAddress }} variant="outlined" size="small"
                error={editingAddress && formData.city.trim() === ''} helperText={editingAddress && formData.city.trim() === '' ? 'Required' : ''} />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              <TextField label="Postcode *" value={editingAddress ? formData.zip : (user.profile?.zip || '')}
                onChange={handleChange('zip')} fullWidth margin="normal"
                InputProps={{ readOnly: !editingAddress }} variant="outlined" size="small"
                error={editingAddress && formData.zip.trim() === ''} helperText={editingAddress && formData.zip.trim() === '' ? 'Required' : ''} />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              {editingAddress ? (
                <Autocomplete<CountryOption, false, false, false>
                  options={countryOptions}
                  loading={countriesLoading}
                  filterOptions={filterCountryOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(a, b) => a.code === b.code && a.label === b.label}
                  value={selectedCountryOption}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({ ...prev, country: newValue?.label ?? '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country *"
                      margin="normal"
                      size="small"
                      variant="outlined"
                      placeholder="Type to search"
                      error={formData.country.trim() === ''}
                      helperText={formData.country.trim() === '' ? 'Required' : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {countriesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  fullWidth
                />
              ) : (
                <TextField label="Country" value={user.profile?.country || ''}
                  fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ── Notification Preferences ── */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h6">Notification Preferences</Typography>
            <Box display="flex" gap={0.5}>
              {sectionEditActions(
                editingNotifications,
                beginEditNotifications,
                handleSaveNotifications,
                cancelNotifications,
                savingAny,
                'notifications',
              )}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap={1} pl={1}>
            <FormControlLabel
              control={<Switch checked={editingNotifications ? formData.emailNotifications : (user.profile?.emailNotifications ?? true)}
                onChange={handleToggle('emailNotifications')} disabled={!editingNotifications} color="secondary" />}
              label="Email Notifications" />
            <FormControlLabel
              control={<Switch checked={editingNotifications ? formData.smsNotifications : (user.profile?.smsNotifications ?? true)}
                onChange={handleToggle('smsNotifications')} disabled={!editingNotifications} color="secondary" />}
              label="SMS Notifications" />
            <FormControlLabel
              control={<Switch checked={editingNotifications ? formData.whatsAppNotifications : (user.profile?.whatsAppNotifications ?? true)}
                onChange={handleToggle('whatsAppNotifications')} disabled={!editingNotifications} color="secondary" />}
              label="WhatsApp Notifications" />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ── Account Information ── */}
          <Typography variant="h6" mb={2}>Account Information</Typography>
          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              <TextField label="Account Status" value={CamelCaseToName(user.status || '')}
                fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
              <TextField label="Account Created" value={formattedDateTime(user.createdAt)}
                fullWidth margin="normal" InputProps={{ readOnly: true }} variant="outlined" size="small" />
            </Box>
          </Box>

          {/* ── Company Memberships & Module Roles ── */}
          {memberships.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" mb={2}>Company Memberships</Typography>
              {memberships.map((m, idx) => (
                <Box key={m.companyId || idx} sx={{
                  mb: 2, p: 2, borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {m.isPrimary && <StarIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />}
                    <Typography variant="subtitle1" fontWeight="bold">{m.companyName || m.companyId}</Typography>
                    {m.isPrimary && (
                      <Chip label="Primary" size="small" sx={{
                        bgcolor: theme.palette.warning.light,
                        color: theme.palette.warning.contrastText || '#000',
                        fontWeight: 600, fontSize: '0.7rem', height: 22,
                      }} />
                    )}
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {m.moduleAndRole && m.moduleAndRole.length > 0 ? (
                      m.moduleAndRole.map((mr, mrIdx) => (
                        <Chip
                          key={mrIdx}
                          label={`${mr.module}: ${CamelCaseToName(mr.role)}`}
                          size="small"
                          sx={{
                            fontWeight: 500, fontSize: '0.75rem',
                            ...(mr.role?.toLowerCase() === 'user'
                              ? {
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                                  color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                                }
                              : {
                                  bgcolor: theme.palette.primary.main,
                                  color: '#fff',
                                }),
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">No modules assigned</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Crop Dialog ── */}
      <Dialog open={cropDialogOpen} onClose={handleCropCancel} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle>Edit Profile Picture</DialogTitle>
        <DialogContent dividers sx={{ p: 0, position: 'relative' }}>
          <Box sx={{ position: 'relative', width: '100%', height: isMobile ? 280 : 400, bgcolor: '#000' }}>
            {cropImageSrc && (
              <Cropper image={cropImageSrc} crop={crop} zoom={zoom} rotation={rotation}
                aspect={1} cropShape="round" showGrid={false}
                onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={onCropComplete} />
            )}
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <ZoomOutIcon fontSize="small" color="action" />
              <Slider value={zoom} min={1} max={3} step={0.05} onChange={(_, v) => setZoom(v as number)} size="small" sx={{ flex: 1 }} />
              <ZoomInIcon fontSize="small" color="action" />
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Tooltip title="Rotate left"><IconButton size="small" onClick={() => setRotation(r => r - 90)}><RotateLeftIcon fontSize="small" /></IconButton></Tooltip>
              <Slider value={rotation} min={0} max={360} step={1} onChange={(_, v) => setRotation(v as number)} size="small" sx={{ flex: 1 }} />
              <Tooltip title="Rotate right"><IconButton size="small" onClick={() => setRotation(r => r + 90)}><RotateRightIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleCropCancel} color="secondary" variant="outlined">Cancel</Button>
          <Button onClick={handleCropConfirm} variant="contained" color="success" disabled={imageUploading}
            startIcon={imageUploading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
